import { channelToFormat } from "../../../../utils/channel-to-format";
import { uuidv4 } from "../../../../utils/uuid";
import { EDITOR_NODES_HEADLESS } from "./nodes-headless";

import type { ChannelName } from "../../../channels/types";
import type { SerialisedLexicalEditorDesign } from "../types/serialised-editor-design";
import type { ChatChannelRecipient } from "./use-chat-channel-recipients";
import type { SerializedLexicalNode } from "lexical";

type Channel = NonNullable<ChannelName>;

/** A serialised node, read for its type and children and otherwise carried as it is. */
interface Node {
  type: string;
  children?: Node[];
  [field: string]: unknown;
}

export interface ConvertDesignOptions {
  /** Slack sends uploaded video only once its app has been updated to. */
  slackVideoEnabled?: boolean;
  /** Names a Slack channel mention by, when it becomes text. */
  slackChannelRecipients?: ChatChannelRecipient[];
  /** The message goes to a chat channel rather than to people, so it cannot be segmented. */
  chatChannelTarget?: boolean;
  /** Node types to leave out wherever they sit, along with everything inside them. */
  exclude?: string[];
}

export interface ConvertedDesign {
  design: SerialisedLexicalEditorDesign;
  /** What the target platform could not hold, by node type, for the caller to report. */
  dropped: { type: string; count: number }[];
}

const CORE_TYPES = ["root", "paragraph", "text", "linebreak", "tab"];

// Lexical text format bits: chat sends only bold and italic.
const IS_BOLD = 1;
const CHAT_TEXT_FORMATS = IS_BOLD | 2;

/** Text nodes, which carry a format and a style however they are typed. */
const TEXT_TYPES = new Set(["text", "merge-tag-v2", "emoji", "non-gsm-highlight"]);

/** Blocks that hold no text, so they count as content of a dynamic container on their own. */
const BLOCK_TYPES = new Set([
  "image",
  "design-huddle",
  "video",
  "divider",
  "buttonlist",
  "styleablebutton",
  "poll",
  "survey-answers",
  "table",
  "data-table",
]);

const text = (value: string, format = 0): Node => ({
  type: "text",
  version: 1,
  text: value,
  format,
  detail: 0,
  mode: "normal",
  style: "",
});

const paragraph = (children: Node[]): Node => ({
  type: "paragraph",
  version: 1,
  children,
  direction: null,
  format: "",
  indent: 0,
  textFormat: 0,
  textStyle: "",
});

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const textContent = (node: Node): string =>
  typeof node.text === "string" ? node.text : (node.children ?? []).map(textContent).join("");

/**
 * Converts a serialised design written for one platform into one another platform's editor can
 * load and send. A node the target registers is kept as it is; one it does not is transformed into
 * the nearest thing it does hold, or dropped and reported. Text always survives. This works on the
 * JSON alone, so the browser and the Worker, which has no DOM to parse with, convert alike.
 */
export function convertDesign(
  from: Channel,
  to: Channel,
  design: SerialisedLexicalEditorDesign | null,
  options: ConvertDesignOptions = {},
): ConvertedDesign {
  const source = channelToFormat(from);
  const format = channelToFormat(to);
  const registered = new Set([
    ...CORE_TYPES,
    ...EDITOR_NODES_HEADLESS[format].map((node) => node.getType()),
  ]);
  const exclude = new Set(options.exclude);
  const reformat = source !== format;
  const dropped = new Map<string, number>();
  // The button lists made from email buttons, which sit in one row in chat as they did in email.
  const fromEmailButtons = new WeakSet<Node>();

  const drop = (node: Node): [] => {
    dropped.set(node.type, (dropped.get(node.type) ?? 0) + 1);
    return [];
  };

  /** A text node's formatting, kept to what the target sends. */
  const textFormatting = (node: Node): Node => {
    if (!reformat || format === "email") return node;
    const bits = typeof node.format === "number" ? node.format : 0;
    return { ...node, format: format === "chat" ? bits & CHAT_TEXT_FORMATS : 0, style: "" };
  };

  const inlines = (nodes: Node[] = []): Node[] => nodes.flatMap(inline);

  function inline(node: Node): Node[] {
    if (exclude.has(node.type)) return drop(node);

    switch (node.type) {
      case "text":
      case "merge-tag-v2":
      case "emoji":
        return [textFormatting(node)];
      case "non-gsm-highlight":
        return format === "sms" ? [node] : [textFormatting({ ...node, type: "text" })];
      case "linebreak":
      case "tab":
        return [node];
      case "link":
        // SMS has no links, only the URL.
        if (format === "sms") return [text(String(node.url))];
        return [{ ...node, children: inlines(node.children) }];
      case "autolink":
      case "surveylink":
        return [{ ...node, children: inlines(node.children) }];
      case "custom-emoji":
        return format === "chat" ? [node] : [text(`:${String(node.name)}:`)];
      case "broadcast":
        return to === "slack" ? [node] : [text(`@${String(node.range)}`)];
      case "channel": {
        if (to === "slack") return [node];
        const name = options.slackChannelRecipients?.find(
          ({ channel_id }) => channel_id === node.channel_id,
        )?.display;
        return [text(`#${name ?? String(node.channel_id)}`)];
      }
      default:
        return registered.has(node.type) ? [node] : drop(node);
    }
  }

  /** A paragraph of the given inlines, aligned and indented only where the target uses it. */
  const toParagraph = (node: Node, children: Node[]): Node =>
    reformat && format !== "email"
      ? { ...node, type: "paragraph", children, format: "", indent: 0 }
      : { ...node, type: "paragraph", children };

  const bold = (nodes: Node[]): Node[] =>
    nodes.map((node) =>
      TEXT_TYPES.has(node.type)
        ? { ...node, format: (typeof node.format === "number" ? node.format : 0) | IS_BOLD }
        : node.children
          ? { ...node, children: bold(node.children) }
          : node,
    );

  /** A list as SMS paragraphs, one an item: `- ` or its number, indented two spaces a level. */
  const listParagraphs = (list: Node, depth: number): Node[] => {
    const start = typeof list.start === "number" ? list.start : 1;
    return (list.children ?? []).flatMap((item, index) => {
      const nested = (item.children ?? []).filter(({ type }) => type === "list");
      const content = (item.children ?? []).filter(({ type }) => type !== "list");
      const marker = list.listType === "number" ? `${start + index}. ` : "- ";
      return [
        ...(content.length
          ? [paragraph([text(`${"  ".repeat(depth)}${marker}`), ...inlines(content)])]
          : []),
        ...nested.flatMap((child) => listParagraphs(child, depth + 1)),
      ];
    });
  };

  const listItems = (list: Node): Node => ({
    ...list,
    children: (list.children ?? []).map((item) => ({
      ...item,
      children: (item.children ?? []).flatMap((child) =>
        child.type === "list" ? [listItems(child)] : inline(child),
      ),
    })),
  });

  /** A table's cells, in reading order, as blocks; a cell's loose text becomes a paragraph. */
  const cellBlocks = (table: Node): Node[] =>
    (table.children ?? []).flatMap((row) =>
      (row.children ?? []).flatMap((cell) => {
        const children = cell.children ?? [];
        const loose = children.filter((child) => !isBlock(child));
        return [...(loose.length ? [paragraph(loose)] : []), ...children.filter(isBlock)].flatMap(
          block,
        );
      }),
    );

  const isBlock = (node: Node): boolean =>
    !TEXT_TYPES.has(node.type) &&
    !["linebreak", "tab", "link", "autolink", "surveylink", "custom-emoji"].includes(node.type) &&
    !["broadcast", "channel"].includes(node.type);

  /**
   * Email holds an image inside a paragraph, as markdown puts it there, but chat and SMS hold blocks
   * only at the top level, so a paragraph's blocks are split out in place, between its text.
   */
  const splitBlocks = (node: Node): Node[] => {
    const parts: Node[] = [];
    let run: Node[] = [];
    const endRun = (): void => {
      if (run.length) parts.push(toParagraph(node, inlines(run)));
      run = [];
    };
    for (const child of node.children ?? []) {
      if (isBlock(child)) {
        endRun();
        parts.push(...block(child));
      } else run.push(child);
    }
    endRun();
    // A paragraph with nothing in it still holds its place.
    return parts.length || (node.children ?? []).length ? parts : [toParagraph(node, [])];
  };

  const buttonText = (label: unknown, url: unknown): Node =>
    paragraph([text(`${String(label)}: ${String(url)}`)]);

  function block(node: Node): Node[] {
    if (exclude.has(node.type)) return drop(node);

    switch (node.type) {
      case "paragraph":
      // Quotes saved before they were dropped load as paragraphs.
      case "quote":
        return format === "email" ? [toParagraph(node, inlines(node.children))] : splitBlocks(node);
      case "heading":
        if (format === "email") return [{ ...node, children: inlines(node.children) }];
        return [
          paragraph(format === "chat" ? bold(inlines(node.children)) : inlines(node.children)),
        ];
      case "list":
        return format === "sms" ? listParagraphs(node, 0) : [listItems(node)];
      case "table":
      case "data-table":
        if (format === "email")
          return [
            {
              ...node,
              children: (node.children ?? []).map((row) => ({
                ...row,
                children: (row.children ?? []).map((cell) => ({
                  ...cell,
                  children: (cell.children ?? []).flatMap((child) =>
                    isBlock(child) ? block(child) : inline(child),
                  ),
                })),
              })),
            },
          ];
        return cellBlocks(node);
      case "styleablebutton":
        if (format === "chat") {
          const list: Node = {
            type: "buttonlist",
            version: 1,
            format: node.format ?? "",
            buttons: [{ id: uuidv4(), text: node.text, url: node.url }],
          };
          fromEmailButtons.add(list);
          return [list];
        }
        if (format === "sms") return [buttonText(node.text, node.url)];
        return [node];
      case "buttonlist": {
        const buttons = Array.isArray(node.buttons)
          ? node.buttons.filter(
              (button): button is { text: unknown; url: unknown } =>
                typeof button === "object" && button !== null,
            )
          : [];
        if (format === "email")
          return buttons.map((button) => ({
            type: "styleablebutton",
            version: 1,
            text: button.text,
            url: button.url,
            buttonType: "primary_button",
            fullWidth: false,
            format: node.format ?? "",
          }));
        if (format === "sms") return buttons.map((button) => buttonText(button.text, button.url));
        return [node];
      }
      case "video":
        if (to === "slack" && !options.slackVideoEnabled) return drop(node);
        return registered.has(node.type) ? [node] : drop(node);
      case "dynamic": {
        const [toolbar, ...rest] = node.children ?? [];
        const hasToolbar = toolbar?.type === "dynamic-toolbar";
        const children = blocks(hasToolbar ? rest : (node.children ?? []));
        // A channel cannot be segmented, so what the container holds goes to everyone.
        if (format === "chat" && options.chatChannelTarget) return children;
        if (!children.some(hasContent)) return drop(node);
        return [{ ...node, children: hasToolbar ? [toolbar, ...children] : children }];
      }
      default:
        if (!isBlock(node)) return [paragraph(inline(node))];
        return registered.has(node.type) ? [node] : drop(node);
    }
  }

  /** Blocks for the target, with the buttons converted from email merged into one row for chat. */
  function blocks(nodes: Node[]): Node[] {
    return nodes.flatMap(block).reduce<Node[]>((merged, node) => {
      const last = merged.at(-1);
      if (last && fromEmailButtons.has(last) && fromEmailButtons.has(node)) {
        const row: Node = {
          ...last,
          buttons: [...asArray(last.buttons), ...asArray(node.buttons)],
        };
        fromEmailButtons.add(row);
        return [...merged.slice(0, -1), row];
      }
      return [...merged, node];
    }, []);
  }

  const hasContent = (node: Node): boolean =>
    BLOCK_TYPES.has(node.type) || textContent(node).trim().length > 0;

  const children = blocks(design?.root?.children ?? []);

  return {
    design: {
      ...design,
      root: {
        type: "root",
        version: 1,
        direction: null,
        format: "",
        indent: 0,
        ...design?.root,
        // The editor will not load an empty root.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- each node is carried as it came or built to its serialised shape, neither of which Node's index signature can show
        children: (children.length ? children : [paragraph([])]) as SerializedLexicalNode[],
      },
    },
    dropped: [...dropped].map(([type, count]) => ({ type, count })),
  };
}
